import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'

export type ScLookupOption = { id: number; nameEn?: string; nameAr?: string; name?: string }

export type PurchaseRequisitionListRow = {
  id: number
  code: string
  date: string
  reference: string
  departmentId: number
  departmentName: string
  estimatedDeliveryDate: string
  status: number
  attachmentPath: string
  branchId: number
}

export type PurchaseRequisitionMaster = {
  id: number
  code: string
  date: string
  reference: string
  departmentId: number
  estimatedDeliveryDate: string
  status: number
  attachmentPath: string
  branchId: number
  createdDate?: string
  createdBy?: number
  updatedDate?: string
  updatedById?: number
  isEditable?: number
  attachmentFile?: File | null
}

export type PurchaseRequisitionLine = {
  id: number
  itemId: number
  quantity: number
  isDeleted: string
}

export type PurchaseRequisitionSavePayload = PurchaseRequisitionMaster & {
  lines: PurchaseRequisitionLine[]
}

function unwrap(payload: any) {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data
    if ('data' in payload) return payload.data
  }
  return payload
}

function toArray(payload: any): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data
  if (raw && typeof raw === 'object' && Array.isArray(raw.items)) return raw.items
  if (raw && typeof raw === 'object' && Array.isArray(raw.result)) return raw.result
  return []
}

function dateOnly(v: unknown): string {
  const s = String(v ?? '')
  return s.includes('T') ? s.split('T')[0] : s
}

function num(v: unknown, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function str(v: unknown) {
  return typeof v === 'string' ? v : String(v ?? '')
}

function mapMaster(raw: any): PurchaseRequisitionMaster {
  return {
    id: num(raw?.id ?? raw?.Id),
    code: str(raw?.code ?? raw?.Code),
    date: dateOnly(raw?.date ?? raw?.Date),
    reference: str(raw?.reference ?? raw?.Reference),
    departmentId: num(raw?.departmentId ?? raw?.DepartmentId),
    estimatedDeliveryDate: dateOnly(raw?.estimatedDeliveryDate ?? raw?.EstimatedDeliveryDate),
    status: num(raw?.status ?? raw?.Status),
    attachmentPath: str(raw?.attachmentPath ?? raw?.AttachmentPath),
    branchId: num(raw?.branchId ?? raw?.BranchId),
    createdDate: str(raw?.createdDate ?? raw?.CreatedDate) || undefined,
    createdBy: raw?.createdBy != null ? num(raw?.createdBy ?? raw?.CreatedBy) : undefined,
    updatedDate: str(raw?.updatedDate ?? raw?.UpdatedDate) || undefined,
    updatedById: raw?.updatedById != null ? num(raw?.updatedById ?? raw?.UpdatedById) : undefined,
    isEditable: raw?.isEditable != null ? num(raw?.isEditable ?? raw?.IsEditable) : undefined,
    attachmentFile: null,
  }
}

function mapListRow(raw: any): PurchaseRequisitionListRow {
  return {
    id: num(raw?.id ?? raw?.Id),
    code: str(raw?.code ?? raw?.Code),
    date: dateOnly(raw?.date ?? raw?.Date),
    reference: str(raw?.reference ?? raw?.Reference),
    departmentId: num(raw?.departmentId ?? raw?.DepartmentId),
    departmentName: str(raw?.departmentName ?? raw?.DepartmentName ?? raw?.department ?? raw?.Department),
    estimatedDeliveryDate: dateOnly(raw?.estimatedDeliveryDate ?? raw?.EstimatedDeliveryDate),
    status: num(raw?.status ?? raw?.Status),
    attachmentPath: str(raw?.attachmentPath ?? raw?.AttachmentPath),
    branchId: num(raw?.branchId ?? raw?.BranchId),
  }
}

function mapLine(raw: any): PurchaseRequisitionLine {
  return {
    id: num(raw?.id ?? raw?.Id),
    itemId: num(raw?.itemId ?? raw?.ItemId),
    quantity: num(raw?.quantity ?? raw?.Quantity),
    isDeleted: str(raw?.isDeleted ?? raw?.IsDeleted ?? 'false'),
  }
}

function buildLinesJson(lines: PurchaseRequisitionLine[]): string {
  return JSON.stringify(lines)
}

function append(formData: FormData, key: string, value: unknown) {
  if (value === undefined || value === null) {
    formData.append(key, '')
    return
  }
  formData.append(key, String(value))
}

function buildSaveUpdateFormData(payload: PurchaseRequisitionSavePayload, requisitionLinesJson: string): FormData {
  const fd = new FormData()
  append(fd, 'Id', payload.id)
  append(fd, 'Code', payload.code)
  append(fd, 'Date', payload.date)
  append(fd, 'Reference', payload.reference)
  append(fd, 'DepartmentId', payload.departmentId)
  append(fd, 'EstimatedDeliveryDate', payload.estimatedDeliveryDate)
  append(fd, 'Status', payload.status)
  append(fd, 'BranchId', payload.branchId)
  append(fd, 'CreatedDate', payload.createdDate ?? new Date().toISOString())
  append(fd, 'CreatedBy', payload.createdBy ?? 0)
  append(fd, 'UpdatedDate', payload.updatedDate ?? new Date().toISOString())
  append(fd, 'UpdatedById', payload.updatedById ?? 0)
  append(fd, 'IsEditable', payload.isEditable ?? 0)
  append(fd, 'AttachmentPath', payload.attachmentPath ?? '')

  append(fd, 'requisitionLinesJson', requisitionLinesJson)

  if (payload.attachmentFile) {
    fd.append('AttachmentFile', payload.attachmentFile)
  } else {
    append(fd, 'AttachmentFile', '')
  }

  return fd
}

export class PurchaseRequisitionService {
  async getAll(branchId: number, pageNumber: number, pageSize: number): Promise<{ items: PurchaseRequisitionListRow[]; totalCount: number }> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.scPurchaseRequisition.getAll(branchId, pageNumber, pageSize))
    const payload = response.data
    const top = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : null
    const raw = unwrap(payload)
    const list = toArray(raw)
    const items = list.map(mapListRow)
    const totalCount = num((raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as any).totalRecords ?? (raw as any).totalCount : undefined) ?? top?.totalRecords ?? top?.totalCount ?? items.length, items.length)
    return { items, totalCount }
  }

  async getById(id: number, branchId: number): Promise<PurchaseRequisitionMaster> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.scPurchaseRequisition.getById(id, branchId))
    const raw = unwrap(response.data)
    const source = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.data) ? raw.data[0] : raw
    return mapMaster(source)
  }

  async getLinesByRequisitionId(id: number, branchId: number): Promise<PurchaseRequisitionLine[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(
      endpoints.scPurchaseRequisition.getRequisitionLinesByRequisitionId(id, branchId),
    )
    return toArray(response.data).map(mapLine)
  }

  async getItems(branchId: number): Promise<{ id: number; name: string }[]> {
    const response = await apiClient.get<ApiResponse<any> | any>(endpoints.scPurchaseRequisition.getItems(branchId))
    const arr = toArray(response.data)
    return arr.map((x: any) => ({
      id: num(x?.itemId ?? x?.ItemId ?? x?.id ?? x?.Id),
      name: str(x?.itemNameEn ?? x?.ItemNameEn ?? x?.itemNameAr ?? x?.ItemNameAr ?? x?.itemName ?? x?.ItemName ?? x?.name ?? x?.Name),
    })).filter((x) => x.id > 0)
  }

  async save(payload: PurchaseRequisitionSavePayload): Promise<void> {
    const requisitionLinesJson = buildLinesJson(payload.lines)
    const fd = buildSaveUpdateFormData(payload, requisitionLinesJson)
    await apiClient.post(endpoints.scPurchaseRequisition.save, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  }

  async update(payload: PurchaseRequisitionSavePayload): Promise<void> {
    const requisitionLinesJson = buildLinesJson(payload.lines)
    const fd = buildSaveUpdateFormData(payload, requisitionLinesJson)
    // Swagger shows PUT; backend usually accepts POST too, but we follow swagger.
    await apiClient.put(endpoints.scPurchaseRequisition.update, fd)
  }

  async delete(id: number): Promise<void> {
    await apiClient.delete(endpoints.scPurchaseRequisition.delete(id))
  }
}

export const purchaseRequisitionService = new PurchaseRequisitionService()

